import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications, generateSound } from '../hooks/useNotifications';
import { Bell, CheckCircle, Package, Volume2, VolumeX } from 'lucide-react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../hooks/useAuth';
import Swal from 'sweetalert2';

const SOUND_OPTIONS = [
    { id: 'pop',     label: 'Pop',     desc: 'Discreto y suave' },
    { id: 'beep',    label: 'Beep',    desc: 'Clásico digital' },
    { id: 'chime',   label: 'Chime',   desc: 'Campana larga' },
    { id: 'crystal', label: 'Crystal', desc: 'Tono cristalino' },
    { id: 'alert',   label: 'Alert',   desc: 'Urgente / alerta' },
];

const NotificationsView = () => {
    const { notifications, unreadCount } = useNotifications();
    const { currentUser } = useAuth();
    const navigate = useNavigate();

    const [soundEnabled, setSoundEnabled] = useState(true);
    const [soundType, setSoundType] = useState('pop');
    const [savingPrefs, setSavingPrefs] = useState(false);

    // Load user prefs from Firestore
    useEffect(() => {
        if (!currentUser?.uid) return;
        getDoc(doc(db, 'users', currentUser.uid)).then(d => {
            if (d.exists() && d.data().preferences) {
                setSoundEnabled(d.data().preferences.soundEnabled ?? true);
                setSoundType(d.data().preferences.soundType || 'pop');
            }
        });
    }, [currentUser]);

    const handleSavePrefs = async () => {
        setSavingPrefs(true);
        try {
            await updateDoc(doc(db, 'users', currentUser.uid), {
                preferences: { soundEnabled, soundType }
            });
            Swal.fire({ title: 'Preferencias guardadas', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
        } catch {
            Swal.fire('Error', 'No se pudieron guardar las preferencias', 'error');
        }
        setSavingPrefs(false);
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return '';
        const d = new Date(timestamp);
        return d.toLocaleDateString('es-ES') + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const getIcon = (type) => {
        if (type === 'alerta_stock') return <Package className="text-amber-500" size={24} />;
        if (type === 'verificacion') return <CheckCircle className="text-indigo-500" size={24} />;
        return <Bell className="text-slate-400" size={24} />;
    };

    return (
        <div className="min-h-screen p-6 text-slate-100 font-sans">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-white mb-1 tracking-tight flex items-center gap-3">
                    <Bell size={28} className="text-emerald-500" />
                    Centro de Notificaciones
                </h1>
                <p className="text-slate-400 text-sm">
                    Tienes {unreadCount} {unreadCount === 1 ? 'alerta pendiente' : 'alertas pendientes'} de acción.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Notificaciones (columna principal) */}
                <div className="lg:col-span-2 space-y-4">
                    {notifications.length === 0 ? (
                        <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-8 text-center">
                            <div className="mx-auto w-16 h-16 bg-slate-700/50 rounded-full flex items-center justify-center mb-4 text-slate-500">
                                <CheckCircle size={32} />
                            </div>
                            <h3 className="text-xl font-bold text-slate-300 mb-2">¡Todo al día!</h3>
                            <p className="text-slate-500">No tienes notificaciones pendientes de revisión.</p>
                        </div>
                    ) : (
                        notifications.map(notif => (
                            <div key={notif.id} className="bg-slate-800/80 border border-slate-700 rounded-xl p-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 hover:border-slate-600 transition-colors">
                                <div className="flex items-start gap-4">
                                    <div className="mt-1">{getIcon(notif.type)}</div>
                                    <div>
                                        <h4 className="font-bold text-white text-lg">{notif.title}</h4>
                                        <p className="text-slate-300 mb-2">{notif.message}</p>
                                        <span className="text-xs text-slate-500">{formatDate(notif.timestamp)}</span>
                                    </div>
                                </div>
                                <button
                                    onClick={() => navigate(notif.link)}
                                    className="w-full md:w-auto bg-slate-700 hover:bg-slate-600 text-white px-6 py-2 rounded-lg font-medium transition-colors whitespace-nowrap"
                                >
                                    Ir a Resolver &rarr;
                                </button>
                            </div>
                        ))
                    )}
                </div>

                {/* Panel lateral: Preferencias de Sonido */}
                <div className="lg:col-span-1">
                    <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 sticky top-6">
                        <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
                            <Volume2 size={20} className="text-emerald-400" />
                            Preferencias de Sonido
                        </h3>
                        <p className="text-slate-400 text-xs mb-5">Elige cómo se anuncian tus alertas</p>

                        {/* Toggle silencio */}
                        <div className="flex items-center justify-between mb-5 p-3 bg-slate-900/50 rounded-xl border border-slate-700">
                            <div>
                                <p className="text-white font-medium text-sm">Sonido Activado</p>
                                <p className="text-slate-500 text-xs">Reproduce un tono al llegar alertas</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input type="checkbox" className="sr-only peer" checked={soundEnabled} onChange={e => setSoundEnabled(e.target.checked)} />
                                <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                            </label>
                        </div>

                        {/* Lista de sonidos */}
                        <div className={`space-y-2 mb-5 transition-opacity ${soundEnabled ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
                            {SOUND_OPTIONS.map(s => (
                                <div
                                    key={s.id}
                                    onClick={() => setSoundType(s.id)}
                                    className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all ${soundType === s.id ? 'bg-emerald-500/10 border-emerald-500/60' : 'bg-slate-900/30 border-slate-700 hover:border-slate-600'}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`w-3 h-3 rounded-full flex-shrink-0 ${soundType === s.id ? 'bg-emerald-500' : 'bg-slate-600'}`} />
                                        <div>
                                            <p className={`text-sm font-bold ${soundType === s.id ? 'text-emerald-300' : 'text-slate-300'}`}>{s.label}</p>
                                            <p className="text-[11px] text-slate-500">{s.desc}</p>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={e => { e.stopPropagation(); generateSound(s.id); }}
                                        className="p-1.5 bg-slate-700 hover:bg-indigo-600 text-slate-300 hover:text-white rounded-lg transition-colors"
                                        title="Probar sonido"
                                    >
                                        <Volume2 size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>

                        {!soundEnabled && (
                            <div className="flex items-center gap-2 text-amber-400 text-xs bg-amber-500/10 border border-amber-500/30 p-2 rounded-lg mb-4">
                                <VolumeX size={14} /> Sonidos silenciados
                            </div>
                        )}

                        <button
                            onClick={handleSavePrefs}
                            disabled={savingPrefs}
                            className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-2.5 rounded-xl font-medium transition-colors disabled:opacity-60"
                        >
                            {savingPrefs ? 'Guardando...' : 'Guardar Preferencias'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NotificationsView;
